import { IRoleSymbols } from 'app/models/permission.model';
import { RoleLegendEntry, roleLegend, staffRoleKey, storedFeatureEnabled } from './role-symbol';

/** Role filter value that shows the receiver teams only (alarm route recipients) */
export const RECEIVER_TEAMS_FILTER = 'receiverTeams';

/** A staff member or receiver team in a picker */
export interface SearchableItem {
  id: string;
  name?: string;
  gsmNumber?: string;
  /** Receiver team: has no role */
  isTeam?: boolean;
  /** Staff member's level, for their built-in role */
  level?: number;
}

/**
 * Search text and role filter of a staff picker (alarm route recipients, receiver team members), so
 * both pickers filter the same way (ES-7564). The search matches the name, ID, phone number and, for
 * staff, the role name. The role filter is a role legend key ("builtIn:<level>" or "custom:<id>"), or
 * RECEIVER_TEAMS_FILTER for teams only.
 */
export class StaffSearch {
  text = '';
  role = '';

  private optionsSource: IRoleSymbols | null | undefined;
  private options: RoleLegendEntry[] = [];

  /**
   * @param symbols The customer's custom roles and who has them, read when filtering
   * @param userLevel Logged-in user's level (Everon admins also see EMAAC accounts)
   * @param translate Translates built-in role names for the search; without it the key is matched
   */
  constructor(private readonly symbols: () => IRoleSymbols | null | undefined,
              private readonly userLevel: () => number | undefined,
              private readonly translate?: { instant(key: string): string },
              private readonly featureEnabled: (feature: string) => boolean = storedFeatureEnabled) { }

  /** Roles to filter by: the same roles, in the same order, as the role legend */
  get roleOptions(): RoleLegendEntry[] {
    const symbols = this.symbols();
    if (this.optionsSource !== symbols || this.options.length === 0) {
      this.optionsSource = symbols;
      this.options = roleLegend(this.featureEnabled, this.userLevel(), symbols);
    }
    return this.options;
  }

  get active(): boolean {
    return !!this.text?.trim() || !!this.role;
  }

  reset(): void {
    this.text = '';
    this.role = '';
  }

  /** Name of a role as the legend shows it: a custom role's name, a built-in role's translated name */
  roleName(roleKey: string): string {
    const entry = this.roleOptions.find(option => option.key === roleKey);
    if (!entry) return '';
    return entry.name ?? (this.translate ? this.translate.instant(entry.nameKey) : entry.nameKey);
  }

  matches(item: SearchableItem): boolean {
    const roleKey = item.isTeam ? null : staffRoleKey(item.id, item.level, this.symbols());
    if (this.role === RECEIVER_TEAMS_FILTER) {
      if (!item.isTeam) return false;
    } else if (this.role && roleKey !== this.role) {
      return false;
    }

    const search = this.text?.trim().toLowerCase();
    if (!search) return true;
    return [item.name, item.id, item.gsmNumber, roleKey ? this.roleName(roleKey) : '']
      .some(value => value?.toString().toLowerCase().includes(search));
  }
}

/** Are all the shown items selected (a select-all checkbox's state) */
export function allShownSelected(selected: readonly string[] | null | undefined | '', shownIds: readonly string[]): boolean {
  const current = new Set(Array.isArray(selected) ? selected : []);
  return shownIds.length > 0 && shownIds.every(id => current.has(id));
}

/**
 * Select all toggle that respects the search: selects the shown items, or unselects them when they are
 * all selected already. Selected items that the search hides stay as they are.
 */
export function toggleShownSelection(selected: readonly string[] | null | undefined | '', shownIds: readonly string[]): string[] {
  const current = Array.isArray(selected) ? [...selected] : [];
  if (allShownSelected(current, shownIds)) {
    const shown = new Set(shownIds);
    return current.filter(id => !shown.has(id));
  }
  return [...current, ...shownIds.filter(id => !current.includes(id))];
}
